import { XCircleIcon } from "@primer/octicons-react";
import { DescriptionList, Pre } from "./common";
import { safeJson } from "./format";

type Pair = [string, string];

const words = (k: string) => k.replace(/_/g, " ");

function scalar(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (Array.isArray(v)) return v.length ? v.map(scalar).join(", ") : "none";
  if (typeof v === "object") return pairs(v).map(([k, x]) => `${k} ${x}`).join(", ");
  return String(v);
}

/** Flattens a record into readable label/value pairs; nested records become "parent child". */
export function pairs(obj: unknown, prefix = ""): Pair[] {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return [];
  const out: Pair[] = [];
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (v === null || v === undefined) continue;
    const label = prefix + words(k);
    if (v && typeof v === "object" && !Array.isArray(v)) out.push(...pairs(v, label + " "));
    else out.push([label, scalar(v)]);
  }
  return out;
}

/** One line of "label value · label value" for tables and timelines. */
export function detailText(obj: unknown): string {
  return pairs(obj)
    .map(([k, v]) => `${k} ${v}`)
    .join(" · ");
}

/** Label/value list, as in a GitHub sidebar or settings page. */
export function DetailList({ value }: { value: unknown }) {
  const p = pairs(value);
  if (!p.length) return null;
  return (
    <DescriptionList className="mt-2">
      {p.map(([k, v]) => (
        <div key={k} className="contents">
          <dt>{k}</dt>
          <dd className="font-mono">{v}</dd>
        </div>
      ))}
    </DescriptionList>
  );
}

type Annotation = { file?: string; line?: string | number; message: string };

/** A CI failure summary shown like GitHub check annotations: file:line and message, plus the log tail. */
export function Annotations({ summary }: { summary: unknown }) {
  const s = (typeof summary === "string" ? safeJson(summary) : summary) as { errors?: Annotation[]; tail?: string; excerpt?: string } | string | null;
  if (!s) return null;
  if (typeof s === "string") return <Pre>{s}</Pre>;
  const tail = s.tail ?? s.excerpt;
  return (
    <div className="mt-2 overflow-hidden rounded-md border bg-background [&>*+*]:border-t [&>*+*]:border-border-muted">
      {s.errors?.map((e, i) => (
        <div className="flex gap-2 px-3 py-2" key={i}>
          <XCircleIcon size={16} className="mt-0.5 shrink-0 text-danger" />
          <div className="min-w-0 flex-1">
            {e.file && (
              <div className="font-mono text-xs">
                {e.file}
                {e.line ? `:${e.line}` : ""}
              </div>
            )}
            <div>{e.message}</div>
          </div>
        </div>
      ))}
      {tail && <Pre className="m-0 rounded-none">{tail}</Pre>}
    </div>
  );
}
